import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Layers, CheckCircle2, AlertTriangle, XCircle, Target, TrendingDown, ArrowRight, Info, Sparkles, ListChecks, ClipboardList, Filter as FilterIcon } from 'lucide-react';
import { Card } from '../../../../../components/ui/Card';
import { Badge } from '../../../../../components/ui/Badge';
import { Select } from '../../../../../components/ui/Select';
import { MODULE_CONFIG } from '../shared';
import { GROUPS, type GroupId } from '../../../../../config/systemConfig';
import type { AssessmentPoolHealthReport, AssessmentPoolSliceFinding, AssessmentPoolFeasibilityFinding } from '../hooks/useGovernanceData';
interface AssessmentPoolHealthTabProps {
  report: AssessmentPoolHealthReport;
  isRunning: boolean;
  onSelectQuestion: (questionId: string) => void;
}
type StatusKind = 'ok' | 'degraded' | 'shortfall' | 'zero' | 'unknown';
interface MatrixCell {
  group: GroupId;
  module: string;
  status: StatusKind;
  finding?: AssessmentPoolFeasibilityFinding;
}
/**
 * AssessmentPoolHealthTab — the full-page "Toetsen" tab.
 *
 * Answers one primary question prominently: "voor welke groep+module-combinatie
 * kunnen we een complete Cito-style toets samenstellen?" — and provides the
 * underlying capacity- and distribution-diagnostics as supporting evidence.
 *
 * Layout:
 *   1. Hero header with overall feasibility readiness
 *   2. KPI strip (4 cards)
 *   3. Feasibility matrix (group × module) — the centerpiece
 *   4. Two-column detail: capacity gaps | distribution issues
 *   5. Decision-hint footer
 */
export function AssessmentPoolHealthTab({
  report,
  isRunning,
  onSelectQuestion
}: AssessmentPoolHealthTabProps) {
  // ─── Derive modules present in the report ──────────────────────────────
  const allModules = useMemo(() => {
    const set = new Set<string>();
    Object.keys(report.byModule).forEach(m => set.add(m));
    report.feasibility.forEach(f => set.add(f.module));
    report.slices.forEach(s => set.add(s.module));
    return Array.from(set).sort();
  }, [report]);
  // ─── Build feasibility matrix (group × module) ─────────────────────────
  const feasibilityIndex = useMemo(() => {
    const map = new Map<string, AssessmentPoolFeasibilityFinding>();
    report.feasibility.forEach(f => map.set(`${f.group}::${f.module}`, f));
    return map;
  }, [report.feasibility]);
  const matrix: MatrixCell[][] = useMemo(() => {
    return GROUPS.map(group => allModules.map(module => {
      const finding = feasibilityIndex.get(`${group}::${module}`);
      let status: StatusKind = 'ok';
      if (!finding) {
        // No finding emitted means either OK or no module-data for that group.
        // We default to 'ok' but downgrade to 'unknown' when there's no
        // module data anywhere in the report (preserves honesty).
        status = report.byModule[module] !== undefined ? 'ok' : 'unknown';
      } else if (finding.reason === 'zero_questions') status = 'zero';else if (finding.reason === 'shortfall') status = 'shortfall';else if (finding.reason === 'degraded') status = 'degraded';
      return {
        group,
        module,
        status,
        finding
      };
    }));
  }, [allModules, feasibilityIndex, report.byModule]);
  // ─── Aggregate matrix counts ──────────────────────────────────────────
  const matrixCounts = useMemo(() => {
    let ok = 0;
    let degraded = 0;
    let shortfall = 0;
    let zero = 0;
    let unknown = 0;
    matrix.forEach(row => row.forEach(cell => {
      if (cell.status === 'ok') ok++;else if (cell.status === 'degraded') degraded++;else if (cell.status === 'shortfall') shortfall++;else if (cell.status === 'zero') zero++;else unknown++;
    }));
    const total = ok + degraded + shortfall + zero + unknown;
    const ready = ok;
    const readinessPct = total > 0 ? Math.round(ready / total * 100) : 0;
    return {
      ok,
      degraded,
      shortfall,
      zero,
      unknown,
      total,
      readinessPct
    };
  }, [matrix]);
  // ─── Filters ─────────────────────────────────────────────────────────
  const [moduleFilter, setModuleFilter] = useState<string>('all');
  const [groupFilter, setGroupFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedCellKey, setSelectedCellKey] = useState<string | null>(null);
  const filteredSlices = useMemo(() => {
    return report.slices.filter(s => {
      if (moduleFilter !== 'all' && s.module !== moduleFilter) return false;
      return true;
    });
  }, [report.slices, moduleFilter]);
  const filteredFeasibility = useMemo(() => {
    return report.feasibility.filter(f => {
      if (moduleFilter !== 'all' && f.module !== moduleFilter) return false;
      if (groupFilter !== 'all' && String(f.group) !== groupFilter) return false;
      if (statusFilter !== 'all' && f.reason !== statusFilter) return false;
      return true;
    });
  }, [report.feasibility, moduleFilter, groupFilter, statusFilter]);
  const selectedCell = useMemo(() => {
    if (!selectedCellKey) return null;
    for (const row of matrix) {
      for (const cell of row) {
        if (`${cell.group}::${cell.module}` === selectedCellKey) return cell;
      }
    }
    return null;
  }, [matrix, selectedCellKey]);
  // ─── Loading state ────────────────────────────────────────────────────
  if (isRunning && allModules.length === 0) {
    return <div className="space-y-6" data-id="element-5086" data-mp="2i3niyokbbtb7">
        <SkeletonHero data-id="element-5087" data-mp="2i71jdr9f1sg0" />
        <SkeletonMatrix data-id="element-5088" data-mp="2d65k5r00eugx" />
      </div>;
  }
  // ─── Empty state (no data, no run yet) ────────────────────────────────
  if (allModules.length === 0) {
    return <Card className="p-10 text-center bg-gradient-to-br from-white to-amber-50/30 border-amber-100" data-id="element-5089" data-mp="28dkosl1gg9ba">
        <Layers className="w-10 h-10 text-amber-400 mx-auto mb-3" data-id="element-5090" data-mp="23fsqn9a7vqyn" />
        <h3 className="text-base font-semibold text-gray-900 mb-1" data-id="element-5091" data-mp="30whz3lto9rh0">
          Nog geen toets-data beschikbaar
        </h3>
        <p className="text-sm text-gray-500 max-w-md mx-auto" data-id="element-5092" data-mp="2w36t1mwopeq5">
          De assessment-pool wordt geanalyseerd zodra de validatie-pipeline
          loopt. Klik op{' '}
          <span className="font-semibold text-gray-700" data-id="element-5093" data-mp="z6kb1s9195u6">Hervalideer</span> in de
          rechterbovenhoek om een verse run te starten.
        </p>
      </Card>;
  }
  return <div className="space-y-6" data-id="element-5094" data-mp="pghgvoq6dlu9">
      {/* ── 1. HERO ── */}
      <HeroHeader counts={matrixCounts} data-id="element-5095" data-mp="pewjlrch7osp" />

      {/* ── 2. KPI STRIP ── */}
      <motion.div initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.3,
      delay: 0.05
    }} className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-5096" data-mp="bfn5cx7tz4rd">
        
        <KpiTile label="Klaar voor toets" value={matrixCounts.ok} suffix={`/ ${matrixCounts.total}`} color="emerald" icon={<CheckCircle2 className="w-4 h-4" data-id="element-5098" data-mp="6jg95w58l7mm" />} hint="Groep × module combinaties die een volledige 30-vragen toets aankunnen." data-id="element-5097" data-mp="1kd6ujgn5fic" />
        
        <KpiTile label="Op het randje" value={matrixCounts.degraded + matrixCounts.shortfall} color="amber" icon={<Target className="w-4 h-4" data-id="element-5100" data-mp="3lcvv6qtzv62l" />} hint="Combinaties met tekort of onbalans — toets is nog op te leveren, maar minder representatief." data-id="element-5099" data-mp="20vwdusidwdpd" />
        
        <KpiTile label="Niet beschikbaar" value={matrixCounts.zero} color="red" icon={<XCircle className="w-4 h-4" data-id="element-5102" data-mp="3v51hpl1h8vae" />} hint="Combinaties met 0 MC-vragen — kan op dit moment geen toets opleveren." data-id="element-5101" data-mp="3gchlz5gnpq2z" />
        
        <KpiTile label="Pool-bevindingen" value={report.affectedSlices} color="indigo" icon={<Layers className="w-4 h-4" data-id="element-5104" data-mp="80vgj5didne5" />} hint="Aantal (module, tier-band) slices met capaciteit- of spreidingsbevindingen." data-id="element-5103" data-mp="3qax9kygh6h7q" />
        
      </motion.div>

      {/* ── 3. FEASIBILITY MATRIX ── */}
      <Card className="p-5" data-id="element-5105" data-mp="hw6sxh1g2iiq">
        <div className="flex items-start justify-between gap-4 mb-4" data-id="element-5106" data-mp="cxvjh7f2aa11">
          <div className="min-w-0" data-id="element-5107" data-mp="cvsmg3fhrgmt">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2" data-id="element-5108" data-mp="2jo1vn75q3cot">
              <Sparkles className="w-4 h-4 text-amber-500" data-id="element-5109" data-mp="2epi4msc9vapl" />
              Toets-feasibility per groep × module
            </h3>
            <p className="text-[11px] text-gray-500 mt-1 leading-relaxed" data-id="element-5110" data-mp="1swuitl3uy918">
              Elke cel toont of een complete 30-vragen Cito-toets opgesteld kan
              worden voor die combinatie. Klik op een cel voor details.
            </p>
          </div>
          <div className="hidden sm:flex items-center gap-3 flex-shrink-0" data-id="element-5111" data-mp="22o8j3v3mvcvf">
            <LegendDot color="emerald" label="Klaar" data-id="element-5112" data-mp="27k5udn01ht1c" />
            <LegendDot color="amber" label="Onbalans" data-id="element-5113" data-mp="19y07km3lrqpt" />
            <LegendDot color="orange" label="Tekort" data-id="element-5114" data-mp="19zb7gatxgebn" />
            <LegendDot color="red" label="0 vragen" data-id="element-5115" data-mp="1excqivrcsdn4" />
            <LegendDot color="gray" label="—" data-id="element-5116" data-mp="1jveark0imj06" />
          </div>
        </div>

        <FeasibilityMatrix modules={allModules} matrix={matrix} selectedCellKey={selectedCellKey} onSelectCell={key => setSelectedCellKey(prev => prev === key ? null : key)} data-id="element-5117" data-mp="1ju3ahu6a3tdt" />
        

        {/* Selected cell detail panel */}
        {selectedCell && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} transition={{
        duration: 0.2
      }} className="mt-4 overflow-hidden" data-id="element-5118" data-mp="2zblr49vb8w5a">
          
            <CellDetailPanel cell={selectedCell} onSelectQuestion={onSelectQuestion} onClose={() => setSelectedCellKey(null)} data-id="element-5119" data-mp="jnq0e85pq2is" />
          
          </motion.div>}

        {/* Legend (mobile) */}
        <div className="flex sm:hidden flex-wrap items-center gap-3 mt-4 pt-3 border-t border-gray-100" data-id="element-5120" data-mp="9wlnddzlkrql">
          <LegendDot color="emerald" label="Klaar" data-id="element-5121" data-mp="4zv6iofyxs44" />
          <LegendDot color="amber" label="Onbalans" data-id="element-5122" data-mp="3w4uvzylakgts" />
          <LegendDot color="orange" label="Tekort" data-id="element-5123" data-mp="3w3a30deml5y1" />
          <LegendDot color="red" label="0 vragen" data-id="element-5124" data-mp="3s2hujmdkv52t" />
          <LegendDot color="gray" label="—" data-id="element-5125" data-mp="3s39pcuzcoys1" />
        </div>
      </Card>

      {/* ── 4. FILTER STRIP ── */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 bg-white p-3 rounded-xl border border-gray-200 shadow-sm" data-id="element-5126" data-mp="3n5zu2ypa5swa">
        <div className="flex items-center gap-2 text-xs font-semibold text-gray-700 px-1" data-id="element-5127" data-mp="1lca8s0x7dipa">
          <FilterIcon className="w-3.5 h-3.5 text-gray-400" data-id="element-5128" data-mp="29wzauix23qdd" />
          Filter
        </div>
        <Select value={moduleFilter} onChange={e => setModuleFilter(e.target.value)} options={[{
        value: 'all',
        label: 'Alle modules'
      }, ...allModules.map(m => ({
        value: m,
        label: m
      }))]} variant="admin" size="sm" fullWidth={false} data-id="element-5129" data-mp="24ynzu4v0z9tk" />
        
        <Select value={groupFilter} onChange={e => setGroupFilter(e.target.value)} options={[{
        value: 'all',
        label: 'Alle groepen'
      }, {
        value: '6',
        label: 'Groep 6'
      }, {
        value: '7',
        label: 'Groep 7'
      }, {
        value: '8',
        label: 'Groep 8'
      }]} variant="admin" size="sm" fullWidth={false} data-id="element-5130" data-mp="1q9hus09iapdr" />
        
        <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} options={[{
        value: 'all',
        label: 'Alle statussen'
      }, {
        value: 'zero_questions',
        label: '0 vragen'
      }, {
        value: 'shortfall',
        label: 'Tekort'
      }, {
        value: 'degraded',
        label: 'Onbalans'
      }]} variant="admin" size="sm" fullWidth={false} data-id="element-5131" data-mp="1v6i1mde6jw5x" />
        
        <div className="flex-1" data-id="element-5132" data-mp="3izttr1iil30z" />
        <span className="text-[11px] text-gray-500 tabular-nums px-1" data-id="element-5133" data-mp="357dqbzlpb2zj">
          {filteredFeasibility.length} feasibility · {filteredSlices.length}{' '}
          pool-slices
        </span>
      </div>

      {/* ── 5. TWO-COLUMN DETAIL ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-5134" data-mp="1qnhlcyqibu5">
        {/* Feasibility findings list */}
        <Card className="p-5" data-id="element-5135" data-mp="5pcz9hqt2fjl">
          <div className="flex items-center justify-between mb-3" data-id="element-5136" data-mp="3nxc5eznx1wco">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2" data-id="element-5137" data-mp="3nz6hdoh7me4m">
              <ClipboardList className="w-4 h-4 text-orange-500" data-id="element-5138" data-mp="3suc4urq9g0ut" />
              Wat ontbreekt voor een toets?
            </h3>
            <span className="text-[10px] text-gray-400 uppercase tracking-wider" data-id="element-5139" data-mp="2hh07mzp0aghm">
              Layer C
            </span>
          </div>
          {filteredFeasibility.length === 0 ? <EmptyTile icon={<CheckCircle2 className="w-6 h-6 text-emerald-400" data-id="element-5141" data-mp="1xgq5vwbx3tjd" />} text="Geen feasibility-bevindingen op deze filter." data-id="element-5140" data-mp="1xgzxtpgiwarp" /> : <div className="space-y-1.5" data-id="element-5142" data-mp="1omlhdjk9au70">
              {filteredFeasibility.map((finding, idx) => <FeasibilityRow key={finding.comboKey} finding={finding} rank={idx + 1} onSelectQuestion={onSelectQuestion} data-id="element-5143" data-mp="1ok8tyqvqbrmk" data-mpi={idx} />)}
            </div>}
        </Card>

        {/* Capacity + Distribution slice findings */}
        <Card className="p-5" data-id="element-5144" data-mp="19pb362g8ba7l">
          <div className="flex items-center justify-between mb-3" data-id="element-5145" data-mp="19o9n1jkcvxj0">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2" data-id="element-5146" data-mp="15ng0atingorp">
              <ListChecks className="w-4 h-4 text-rose-500" data-id="element-5147" data-mp="10omlmxrmmrpy" />
              Pool-capaciteit & spreiding
            </h3>
            <span className="text-[10px] text-gray-400 uppercase tracking-wider" data-id="element-5148" data-mp="302a8mn4jmfdm">
              Layer A · B
            </span>
          </div>
          {/* Layer A/B stat tiles */}
          <div className="grid grid-cols-4 gap-1.5 mb-3" data-id="element-5149" data-mp="32m6iow4zrej7">
            <MiniStat label="Gat" count={report.capacityCounts.gap} tone="red" data-id="element-5150" data-mp="32lnc2bkskckh" />
            
            <MiniStat label="Dun" count={report.capacityCounts.thin} tone="amber" data-id="element-5151" data-mp="37jovixm4qdwh" />
            
            <MiniStat label="Cito-scheef" count={report.distributionCounts.citoConcentration} tone="orange" data-id="element-5152" data-mp="3cdb1k6xsfjjb" />
            
            <MiniStat label="DOK-smal" count={report.distributionCounts.dokCoverage} tone="rose" data-id="element-5153" data-mp="3hd5ojzefzoz3" />
            
          </div>
          {filteredSlices.length === 0 ? <EmptyTile icon={<CheckCircle2 className="w-6 h-6 text-emerald-400" data-id="element-5155" data-mp="2on6yc0oihlsu" />} text="Geen pool-bevindingen op deze filter." data-id="element-5154" data-mp="2ongmdipzxivw" /> : <div className="space-y-1.5 max-h-[480px] overflow-y-auto pr-1" data-id="element-5156" data-mp="2tjxbaf50s1a1">
              {filteredSlices.map((slice, idx) => <SliceRow key={slice.sliceKey} slice={slice} rank={idx + 1} onSelectQuestion={onSelectQuestion} data-id="element-5157" data-mp="2xoepzzwivody" data-mpi={idx} />)}
            </div>}
        </Card>
      </div>

      {/* ── 6. DECISION HINT FOOTER ── */}
      <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-amber-50/60 border border-amber-100" data-id="element-5158" data-mp="cv1nv5gywcez">
        <Info className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" data-id="element-5159" data-mp="bhs69s0syxa3" />
        <p className="text-xs text-amber-900 leading-relaxed" data-id="element-5160" data-mp="6i5qbatlb2xl">
          <span className="font-semibold" data-id="element-5161" data-mp="leep6b9kje9h">Aanpakvolgorde:</span> begin met{' '}
          <span className="font-semibold" data-id="element-5162" data-mp="gesadxe3dpyk">0 vragen</span> (rode cellen) en{' '}
          <span className="font-semibold" data-id="element-5163" data-mp="ggvaja8ykzh0">pool-gat</span> — die kunnen nu al
          geen toets opleveren. <em data-id="element-5164" data-mp="ucgswykosl0g">Tekort</em> en <em data-id="element-5165" data-mp="pfgmunnu7i9c">dun</em> werken op het
          randje. <em data-id="element-5166" data-mp="pef9udn9gloz">Onbalans</em> en <em data-id="element-5167" data-mp="pf6wv02slb3n">cito-scheef</em> blijven functioneel
          maar minder representatief voor de hele curriculumstrook. Alle
          bevindingen zijn diagnostisch (coach/info) — nooit blocker.
        </p>
      </div>
    </div>;
}
// ─── Hero Header ──────────────────────────────────────────────────────────
function HeroHeader({
  counts
}: {
  counts: {
    ok: number;
    degraded: number;
    shortfall: number;
    zero: number;
    unknown: number;
    total: number;
    readinessPct: number;
  };
}) {
  const allReady = counts.ok === counts.total && counts.total > 0;
  const ringColor = allReady ? 'text-emerald-500' : counts.zero > 0 ? 'text-red-500' : 'text-amber-500';
  const ringBg = allReady ? 'from-emerald-50 to-white' : counts.zero > 0 ? 'from-red-50 to-white' : 'from-amber-50 to-white';
  return <motion.div initial={{
    opacity: 0,
    y: -4
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.3
  }} className={`relative overflow-hidden rounded-2xl border border-gray-200 bg-gradient-to-br ${ringBg} p-6`} data-id="element-5168" data-mp="1ns9cpfjwzq19">
      
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6" data-id="element-5169" data-mp="101ddhxd4psjz">
        {/* Readiness ring */}
        <div className="relative flex-shrink-0" data-id="element-5170" data-mp="142yx01k65535">
          <svg width="96" height="96" viewBox="0 0 96 96" className="rotate-[-90deg]" data-id="element-5171" data-mp="q2ec8zup0z70">
            
            <circle cx="48" cy="48" r="42" fill="none" stroke="currentColor" strokeWidth="8" className="text-gray-200" data-id="element-5172" data-mp="v1hi2mihz9g1" />
            
            <motion.circle cx="48" cy="48" r="42" fill="none" stroke="currentColor" strokeWidth="8" strokeLinecap="round" className={ringColor} initial={{
            pathLength: 0
          }} animate={{
            pathLength: counts.readinessPct / 100
          }} transition={{
            duration: 0.8,
            ease: 'easeOut'
          }} style={{
            strokeDasharray: 2 * Math.PI * 42
          }} data-id="element-5173" data-mp="2wqwxsl379qel" />
            
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-5174" data-mp="3fnerh81m00dh">
            <span className="text-2xl font-bold text-gray-900 tabular-nums leading-none" data-id="element-5175" data-mp="3bop5ir2i9ai8">
              {counts.readinessPct}%
            </span>
            <span className="text-[10px] text-gray-500 mt-1 uppercase tracking-wider" data-id="element-5176" data-mp="31q9hiwyryhcy">
              klaar
            </span>
          </div>
        </div>

        {/* Header text */}
        <div className="flex-1 min-w-0" data-id="element-5177" data-mp="1qakvq8x9bodq">
          <div className="flex items-center gap-2 mb-1.5" data-id="element-5178" data-mp="1q9t7xk9wiuy0">
            <Layers className="w-4 h-4 text-amber-500" data-id="element-5179" data-mp="1g6w9wlxq7wy3" />
            <span className="text-[10px] font-bold text-amber-600 uppercase tracking-wider" data-id="element-5180" data-mp="1g8h5mevs1lxj">
              Assessment Pool · Stage 6b
            </span>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-1" data-id="element-5181" data-mp="1q4k8xo2d7qm2">
            Toets-feasibility
          </h2>
          <p className="text-sm text-gray-600 leading-relaxed max-w-2xl" data-id="element-5182" data-mp="1uwln5fj3freq">
            {allReady ? <>
                <span className="font-semibold text-emerald-700" data-id="element-5183" data-mp="1ywcd5h0sq3i7">
                  Alle {counts.total} groep × module combinaties
                </span>{' '}
                kunnen een volledige Cito-style toets opleveren. Pool-capaciteit
                en spreiding zijn gezond.
              </> : <>
                <span className="font-semibold text-gray-900" data-id="element-5184" data-mp="2dtwdgamqzai1">{counts.ok}</span>{' '}
                van{' '}
                <span className="font-semibold text-gray-900" data-id="element-5185" data-mp="ej50h7yye1z6">
                  {counts.total}
                </span>{' '}
                combinaties kan nu een volledige toets opleveren.{' '}
                {counts.zero > 0 && <>
                    <span className="text-red-700 font-semibold" data-id="element-5186" data-mp="2gf43dhtx17v3">
                      {counts.zero}
                    </span>{' '}
                    {counts.zero === 1 ? 'combinatie' : 'combinaties'} heeft
                    geen MC-vragen.{' '}
                  </>}
                {counts.degraded + counts.shortfall > 0 && <>
                    <span className="text-amber-700 font-semibold" data-id="element-5187" data-mp="22funykdjahs6">
                      {counts.degraded + counts.shortfall}
                    </span>{' '}
                    {counts.degraded + counts.shortfall === 1 ? 'staat' : 'staan'}{' '}
                    op het randje.
                  </>}
              </>}
          </p>
        </div>
      </div>
    </motion.div>;
}
// ─── KPI Tile ─────────────────────────────────────────────────────────────
function KpiTile({
  label,
  value,
  suffix,
  color,
  icon,
  hint
}: {
  label: string;
  value: number;
  suffix?: string;
  color: 'emerald' | 'amber' | 'red' | 'indigo';
  icon: React.ReactNode;
  hint: string;
}) {
  const palette = {
    emerald: {
      bg: 'bg-emerald-50',
      border: 'border-emerald-100',
      text: 'text-emerald-700',
      iconBg: 'bg-emerald-100'
    },
    amber: {
      bg: 'bg-amber-50',
      border: 'border-amber-100',
      text: 'text-amber-700',
      iconBg: 'bg-amber-100'
    },
    red: {
      bg: 'bg-red-50',
      border: 'border-red-100',
      text: 'text-red-700',
      iconBg: 'bg-red-100'
    },
    indigo: {
      bg: 'bg-indigo-50',
      border: 'border-indigo-100',
      text: 'text-indigo-700',
      iconBg: 'bg-indigo-100'
    }
  }[color];
  return <div className={`relative ${palette.bg} ${palette.border} border rounded-xl p-3 group`} title={hint} data-id="element-5188" data-mp="86xt38ng0ld2">
      
      <div className="flex items-start justify-between mb-2" data-id="element-5189" data-mp="vwjt1ybn34m4">
        <div className={`w-7 h-7 rounded-lg ${palette.iconBg} ${palette.text} flex items-center justify-center`} data-id="element-5190" data-mp="vuyy47lexjnq">
          
          {icon}
        </div>
      </div>
      <p className={`text-2xl font-bold ${palette.text} tabular-nums leading-none`} data-id="element-5191" data-mp="2s94nnxkgtgu3">
        
        {value}
        {suffix && <span className="text-sm font-medium ml-1 opacity-70" data-id="element-5192" data-mp="2x9sh0pw629rh">{suffix}</span>}
      </p>
      <p className={`text-xs font-medium ${palette.text} mt-1.5`} data-id="element-5193" data-mp="2iijj7ti41i39">{label}</p>
    </div>;
}
// ─── Feasibility Matrix ──────────────────────────────────────────────────
function FeasibilityMatrix({
  modules,
  matrix,
  selectedCellKey,
  onSelectCell
}: {
  modules: string[];
  matrix: MatrixCell[][];
  selectedCellKey: string | null;
  onSelectCell: (key: string) => void;
}) {
  return <div className="overflow-x-auto -mx-2 px-2" data-id="element-5194" data-mp="1zli95u9p8rsg">
      <table className="min-w-full border-separate border-spacing-1" data-id="element-5195" data-mp="24is7jzf2gdsf">
        <thead data-id="element-5196" data-mp="24l4oc9938i3w">
          <tr data-id="element-5197" data-mp="3txyorlodxi9z">
            <th className="text-[10px] font-bold text-gray-400 uppercase tracking-wider text-left pl-2 pb-1 w-16" data-id="element-5198" data-mp="3txgjy0nnpvqg">
              Groep
            </th>
            {modules.map(module => {
            const cfg = MODULE_CONFIG[module];
            const bg = cfg?.bgClass ?? 'bg-gray-400';
            return <th key={module} className="text-[10px] font-bold text-gray-600 text-center pb-1 px-0.5 min-w-[78px]" data-id="element-5199" data-mp="1iav3e24jg9eh">
                  
                  <div className="flex flex-col items-center gap-1" data-id="element-5200" data-mp="1x0artbglk2r0">
                    <div className={`w-1.5 h-1.5 rounded-full ${bg}`} data-id="element-5201" data-mp="1s89dzl3s63yv" />
                    <span className="font-mono" data-id="element-5202" data-mp="2607dr5blqnid">{module}</span>
                  </div>
                </th>;
          })}
          </tr>
        </thead>
        <tbody data-id="element-5203" data-mp="2102y1hiipwt7">
          {matrix.map((row, rowIdx) => {
          const group = row[0]?.group;
          return <tr key={group ?? rowIdx} data-id="element-5204" data-mp="2fx2bvn3yqlw4" data-mpi={rowIdx}>
                <td className="text-xs font-semibold text-gray-700 pl-2 pr-1 align-middle" data-id="element-5205" data-mp="2fw0y3avkblap">
                  Groep {group}
                </td>
                {row.map((cell, idx) => {
              const key = `${cell.group}::${cell.module}`;
              const selected = key === selectedCellKey;
              return <td key={key} className="p-0" data-id="element-5206" data-mp="169eqwe03uesf" data-mpi={idx}>
                      <motion.button type="button" onClick={() => onSelectCell(key)} initial={{
                  opacity: 0,
                  scale: 0.92
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} transition={{
                  duration: 0.2,
                  delay: 0.02 * (rowIdx * modules.length + idx)
                }} whileHover={{
                  scale: 1.04
                }} whileTap={{
                  scale: 0.97
                }} className={`w-full ${getCellClass(cell.status, selected)}`} aria-label={`${cell.module} groep ${cell.group} — ${getStatusLabel(cell.status)}`} data-id="element-5207" data-mp="16aq1oxrywmt1">
                        
                        <CellContent cell={cell} data-id="element-5208" data-mp="22ymd7o8jsrxe" />
                      </motion.button>
                    </td>;
            })}
              </tr>;
        })}
        </tbody>
      </table>
    </div>;
}
function getCellClass(status: StatusKind, selected: boolean): string {
  const ring = selected ? 'ring-2 ring-offset-1 ring-gray-900' : '';
  if (status === 'ok') return `${ring} h-14 rounded-lg bg-emerald-50 border border-emerald-200 hover:border-emerald-400 hover:bg-emerald-100 transition-all flex items-center justify-center`;
  if (status === 'degraded') return `${ring} h-14 rounded-lg bg-amber-50 border border-amber-200 hover:border-amber-400 hover:bg-amber-100 transition-all flex items-center justify-center`;
  if (status === 'shortfall') return `${ring} h-14 rounded-lg bg-orange-50 border border-orange-200 hover:border-orange-400 hover:bg-orange-100 transition-all flex items-center justify-center`;
  if (status === 'zero') return `${ring} h-14 rounded-lg bg-red-50 border border-red-200 hover:border-red-400 hover:bg-red-100 transition-all flex items-center justify-center`;
  return `${ring} h-14 rounded-lg bg-gray-50 border border-gray-200 hover:border-gray-300 transition-all flex items-center justify-center`;
}
function getStatusLabel(status: StatusKind): string {
  if (status === 'ok') return 'klaar';
  if (status === 'degraded') return 'onbalans';
  if (status === 'shortfall') return 'tekort';
  if (status === 'zero') return '0 vragen';
  return 'geen data';
}
function CellContent({
  cell
}: {
  cell: MatrixCell;
}) {
  if (cell.status === 'ok') {
    return <CheckCircle2 className="w-4 h-4 text-emerald-600" data-id="element-5209" data-mp="dj4trd0dh2ad" />;
  }
  if (cell.status === 'unknown') {
    return <span className="text-gray-400 text-xs" data-id="element-5210" data-mp="118pfu4rtofnv">—</span>;
  }
  const f = cell.finding;
  if (!f) return null;
  if (f.reason === 'zero_questions') {
    return <div className="flex flex-col items-center leading-none" data-id="element-5211" data-mp="16aeoxevhij9b">
        <XCircle className="w-3.5 h-3.5 text-red-600 mb-0.5" data-id="element-5212" data-mp="rie59zdmv880" />
        <span className="text-[10px] font-bold text-red-700" data-id="element-5213" data-mp="wi8yi9kvs9w8">0</span>
      </div>;
  }
  if (typeof f.achievable === 'number' && typeof f.target === 'number') {
    const color = f.reason === 'shortfall' ? 'text-orange-700' : 'text-amber-700';
    return <div className="flex flex-col items-center leading-tight" data-id="element-5214" data-mp="1p0eahrs49g7z">
        <span className={`text-[11px] font-bold ${color} tabular-nums`} data-id="element-5215" data-mp="39op0gjdvdev4">
          {f.achievable}/{f.target}
        </span>
        <span className="text-[9px] text-gray-500 mt-0.5" data-id="element-5216" data-mp="34pcgq7iaonwr">
          {f.reason === 'shortfall' ? 'tekort' : 'onbalans'}
        </span>
      </div>;
  }
  return <AlertTriangle className="w-4 h-4 text-amber-600" data-id="element-5217" data-mp="3efyoxgnomc5f" />;
}
// ─── Cell Detail Panel ───────────────────────────────────────────────────
function CellDetailPanel({
  cell,
  onSelectQuestion,
  onClose
}: {
  cell: MatrixCell;
  onSelectQuestion: (questionId: string) => void;
  onClose: () => void;
}) {
  const cfg = MODULE_CONFIG[cell.module];
  const bg = cfg?.bgClass ?? 'bg-gray-400';
  const f = cell.finding;
  return <div className="rounded-xl border border-gray-200 bg-white p-4" data-id="element-5218" data-mp="2voxrix27monz">
      <div className="flex items-start justify-between gap-3 mb-3" data-id="element-5219" data-mp="f0tos367vd4f">
        <div className="flex items-center gap-3 min-w-0" data-id="element-5220" data-mp="ez0g6ic1mits">
          <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${bg}`} data-id="element-5221" data-mp="b000v9srxybj" />
          <div data-id="element-5222" data-mp="33tp4rtpattv5">
            <h4 className="text-sm font-semibold text-gray-900" data-id="element-5223" data-mp="33u8dqkwz17x1">
              Groep {cell.group} ·{' '}
              <span className="font-mono" data-id="element-5224" data-mp="38s0fs7rbyaey">{cell.module}</span>
            </h4>
            <p className="text-[11px] text-gray-500 mt-0.5" data-id="element-5225" data-mp="3ds5ygyfmgmkw">
              {getStatusLabel(cell.status)}
            </p>
          </div>
        </div>
        <button onClick={onClose} className="text-xs text-gray-400 hover:text-gray-600 px-2 py-1 rounded transition-colors" data-id="element-5226" data-mp="3hsy8htwbivi8">
          
          Sluiten
        </button>
      </div>

      {cell.status === 'ok' ? <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-100" data-id="element-5227" data-mp="2q2tv4f6z78qi">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" data-id="element-5228" data-mp="2u1su7j610ba1" />
          <p className="text-xs text-emerald-800" data-id="element-5229" data-mp="2yw6spufziyix">
            Deze combinatie kan een volledige 30-vragen Cito-style toets
            opleveren met de gewenste tier-band verhouding (35% laag · 40%
            midden · 25% hoog).
          </p>
        </div> : cell.status === 'unknown' ? <p className="text-xs text-gray-500" data-id="element-5230" data-mp="1ytqx2gr1xb1g">
          Geen pool-data beschikbaar voor deze combinatie in de huidige run.
        </p> : f ? <div className="space-y-3" data-id="element-5231" data-mp="1ttmeml6l268l">
          {/* Target vs achievable bars */}
          {typeof f.target === 'number' && <div data-id="element-5232" data-mp="1pvygzadi9zd0">
              <div className="flex items-center justify-between text-[11px] mb-1" data-id="element-5233" data-mp="1kvaosldjjch7">
                <span className="text-gray-600 font-medium" data-id="element-5234" data-mp="1fvxsla0s4t1y">
                  Bereikbaar / Doel
                </span>
                <span className="text-gray-900 font-semibold tabular-nums" data-id="element-5235" data-mp="1b4pjyg79xbpu">
                  {f.achievable ?? 0} / {f.target}
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-5236" data-mp="163s1s3m9khoc">
                <div className={`h-full rounded-full ${f.reason === 'zero_questions' ? 'bg-red-500' : f.reason === 'shortfall' ? 'bg-orange-500' : 'bg-amber-500'}`} style={{
            width: `${Math.min(100, (f.achievable ?? 0) / f.target * 100)}%`
          }} data-id="element-5237" data-mp="126dvoktvqq0k" />
            
              </div>
            </div>}

          {/* Per-band breakdown */}
          {f.bandAchievable && f.bandTarget && <div className="grid grid-cols-3 gap-2" data-id="element-5238" data-mp="2l1ma66j3ywvk">
              <BandTile label="Laag" achievable={f.bandAchievable.low} target={f.bandTarget.low} isEmpty={f.emptyBands?.includes('low')} data-id="element-5239" data-mp="272mw1st820dn" />
          
              <BandTile label="Midden" achievable={f.bandAchievable.mid} target={f.bandTarget.mid} isEmpty={f.emptyBands?.includes('mid')} data-id="element-5240" data-mp="2bzwkb54vk5zw" />
          
              <BandTile label="Hoog" achievable={f.bandAchievable.high} target={f.bandTarget.high} isEmpty={f.emptyBands?.includes('high')} data-id="element-5241" data-mp="223tjc2lredej" />
          
            </div>}

          {/* CTA */}
          {f.issue.questionId && f.issue.questionId !== 'BATCH' && f.issue.questionId !== 'UNKNOWN' && <button onClick={() => onSelectQuestion(f.issue.questionId)} className="w-full text-left flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-100 hover:bg-amber-50 hover:border-amber-200 transition-colors group" data-id="element-5242" data-mp="1edpdrpp0g3kw">
          
                <span className="text-xs text-gray-700" data-id="element-5243" data-mp="zt37shv4e1nz">
                  Bekijk representatieve vraag in{' '}
                  <span className="font-semibold" data-id="element-5244" data-mp="uua0j76ff6w9">Vragen</span>
                </span>
                <ArrowRight className="w-3.5 h-3.5 text-gray-400 group-hover:text-amber-600 group-hover:translate-x-0.5 transition-all" data-id="element-5245" data-mp="19n3kp5wn8b79" />
              </button>}
        </div> : null}
    </div>;
}
function BandTile({
  label,
  achievable,
  target,
  isEmpty
}: {
  label: string;
  achievable: number;
  target: number;
  isEmpty?: boolean;
}) {
  const ratio = target > 0 ? achievable / target : 0;
  const status = isEmpty || achievable === 0 ? 'red' : ratio < 1 ? 'amber' : 'emerald';
  const palette = {
    red: 'bg-red-50 border-red-100 text-red-700',
    amber: 'bg-amber-50 border-amber-100 text-amber-700',
    emerald: 'bg-emerald-50 border-emerald-100 text-emerald-700'
  }[status];
  return <div className={`${palette} border rounded-lg p-2 text-center`} data-id="element-5246" data-mp="1xgloqfyxqa32">
      <p className="text-[9px] font-bold uppercase tracking-wider opacity-80" data-id="element-5247" data-mp="2ysectsjyniix">
        {label}
      </p>
      <p className="text-sm font-bold tabular-nums mt-0.5" data-id="element-5248" data-mp="2tvnzhczjj117">
        {achievable}
        <span className="text-[10px] opacity-60" data-id="element-5249" data-mp="2oui6de1yxbr5">/{target}</span>
      </p>
    </div>;
}
// ─── Row components ──────────────────────────────────────────────────────
function FeasibilityRow({
  finding,
  rank,
  onSelectQuestion
}: {
  finding: AssessmentPoolFeasibilityFinding;
  rank: number;
  onSelectQuestion: (questionId: string) => void;
}) {
  const cfg = MODULE_CONFIG[finding.module];
  const bg = cfg?.bgClass ?? 'bg-gray-400';
  const questionId = finding.issue.questionId;
  const hasQuestion = questionId && questionId !== 'BATCH' && questionId !== 'UNKNOWN';
  const tone: 'red' | 'orange' | 'amber' = finding.reason === 'zero_questions' ? 'red' : finding.reason === 'shortfall' ? 'orange' : 'amber';
  const tagLabel = finding.reason === 'zero_questions' ? '0 vragen' : finding.reason === 'shortfall' ? 'tekort' : 'onbalans';
  const toneClasses: Record<'red' | 'orange' | 'amber', string> = {
    red: 'text-red-700 bg-red-50 border-red-100',
    orange: 'text-orange-700 bg-orange-50 border-orange-100',
    amber: 'text-amber-700 bg-amber-50 border-amber-100'
  };
  return <button type="button" disabled={!hasQuestion} onClick={() => hasQuestion && onSelectQuestion(questionId)} className={`group w-full text-left flex items-center gap-3 px-2.5 py-2 rounded-lg border border-gray-100 bg-white hover:border-amber-200 hover:bg-amber-50/40 transition-colors ${hasQuestion ? 'cursor-pointer' : 'cursor-default'}`} data-id="element-5250" data-mp="ln9dhf8olf9k">
      
      <span className="text-[10px] font-bold text-gray-400 w-5 text-center flex-shrink-0" data-id="element-5251" data-mp="3u18amk7e36on">
        {rank}
      </span>
      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${bg}`} data-id="element-5252" data-mp="2pq5mexym2oq" />
      <div className="min-w-0 flex-1" data-id="element-5253" data-mp="31dcephpn9isx">
        <div className="flex items-center gap-2 flex-wrap" data-id="element-5254" data-mp="36cysbs9dx129">
          <span className="font-mono text-xs font-semibold text-gray-900" data-id="element-5255" data-mp="36bdv1uvor40p">
            Groep {finding.group}
          </span>
          <span className="font-mono text-[10px] text-gray-500" data-id="element-5256" data-mp="dg5ervgmlhkl">
            / {finding.module}
          </span>
          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${toneClasses[tone]}`} data-id="element-5257" data-mp="masce5g1spma">
            
            {tagLabel}
          </span>
        </div>
        {typeof finding.achievable === 'number' && typeof finding.target === 'number' ? <p className="text-[10px] text-gray-500 mt-0.5" data-id="element-5258" data-mp="3qpju24kdjr1w">
            {finding.achievable}/{finding.target} bereikbaar
            {finding.emptyBands && finding.emptyBands.length > 0 && <>
                {' '}
                · lege banden:{' '}
                <span className="font-mono text-amber-700" data-id="element-5259" data-mp="3uqvezwsazo3r">
                  {finding.emptyBands.join(', ')}
                </span>
              </>}
          </p> : finding.reason === 'zero_questions' ? <p className="text-[10px] text-gray-500 mt-0.5" data-id="element-5260" data-mp="1a0mrktnuaq9p">
            0 MC-vragen ·{' '}
            <span className="text-red-600 font-semibold" data-id="element-5261" data-mp="1ihenx1kuiw28">
              +{finding.target ?? 30} nodig
            </span>
          </p> : null}
      </div>
      {hasQuestion && <ArrowRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-amber-500 group-hover:translate-x-0.5 transition-all flex-shrink-0" data-id="element-5262" data-mp="1mkbcra0zboem" />}
    </button>;
}
function SliceRow({
  slice,
  rank,
  onSelectQuestion
}: {
  slice: AssessmentPoolSliceFinding;
  rank: number;
  onSelectQuestion: (questionId: string) => void;
}) {
  const cfg = MODULE_CONFIG[slice.module];
  const bg = cfg?.bgClass ?? 'bg-gray-400';
  const representativeQuestion = slice.issues.find(i => i.questionId)?.questionId;
  const tags: Array<{
    label: string;
    tone: 'red' | 'amber' | 'orange' | 'rose';
  }> = [];
  if (slice.capacityStatus === 'gap') tags.push({
    label: 'gat',
    tone: 'red'
  });else if (slice.capacityStatus === 'thin') tags.push({
    label: 'dun',
    tone: 'amber'
  });
  if (slice.distributionReasons.includes('cito_concentration')) tags.push({
    label: 'cito-scheef',
    tone: 'orange'
  });
  if (slice.distributionReasons.includes('dok_coverage')) tags.push({
    label: 'dok-smal',
    tone: 'rose'
  });
  const toneClasses: Record<'red' | 'amber' | 'orange' | 'rose', string> = {
    red: 'text-red-700 bg-red-50 border-red-100',
    amber: 'text-amber-700 bg-amber-50 border-amber-100',
    orange: 'text-orange-700 bg-orange-50 border-orange-100',
    rose: 'text-rose-700 bg-rose-50 border-rose-100'
  };
  return <button type="button" disabled={!representativeQuestion} onClick={() => representativeQuestion && onSelectQuestion(representativeQuestion)} className={`group w-full text-left flex items-center gap-3 px-2.5 py-2 rounded-lg border border-gray-100 bg-white hover:border-amber-200 hover:bg-amber-50/40 transition-colors ${representativeQuestion ? 'cursor-pointer' : 'cursor-default'}`} data-id="element-5263" data-mp="1f0k69pupanmx">
      
      <span className="text-[10px] font-bold text-gray-400 w-5 text-center flex-shrink-0" data-id="element-5264" data-mp="1xzend5hn0069">
        {rank}
      </span>
      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${bg}`} data-id="element-5265" data-mp="25b2rw04d3p79" />
      <div className="min-w-0 flex-1" data-id="element-5266" data-mp="25cxdzhrddncx">
        <div className="flex items-center gap-2 flex-wrap" data-id="element-5267" data-mp="2a8uimqt9t59v">
          <span className="font-mono text-xs font-semibold text-gray-900" data-id="element-5268" data-mp="2abh13lhp956v">
            {slice.module}
          </span>
          <span className="font-mono text-[10px] text-gray-500" data-id="element-5269" data-mp="2k7k38rcjx3tt">
            / {slice.tierBand}
          </span>
          {tags.map(t => <span key={t.label} className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${toneClasses[t.tone]}`} data-id="element-5270" data-mp="2o8dqrjm30gm6">
            
              {t.label}
            </span>)}
        </div>
        {typeof slice.mcEligibleCount === 'number' && typeof slice.minimumViableSize === 'number' && <p className="text-[10px] text-gray-500 mt-0.5" data-id="element-5271" data-mp="32ykyp5egqh8m">
              {slice.mcEligibleCount} MC-vragen · minimum{' '}
              {slice.minimumViableSize}
              {typeof slice.remediationNeeded === 'number' && slice.remediationNeeded > 0 && <>
                    {' '}
                    ·{' '}
                    <span className="text-red-600 font-semibold" data-id="element-5272" data-mp="1cv185ez2o781">
                      +{slice.remediationNeeded} nodig
                    </span>
                  </>}
            </p>}
      </div>
      {representativeQuestion && <ArrowRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-amber-500 group-hover:translate-x-0.5 transition-all flex-shrink-0" data-id="element-5273" data-mp="1lo4g57qy7rva" />}
    </button>;
}
// ─── Small helpers ───────────────────────────────────────────────────────
function MiniStat({
  label,
  count,
  tone
}: {
  label: string;
  count: number;
  tone: 'red' | 'amber' | 'orange' | 'rose';
}) {
  const palette = {
    red: 'bg-red-50 border-red-100 text-red-700',
    amber: 'bg-amber-50 border-amber-100 text-amber-700',
    orange: 'bg-orange-50 border-orange-100 text-orange-700',
    rose: 'bg-rose-50 border-rose-100 text-rose-700'
  }[tone];
  return <div className={`${palette} border rounded-lg px-1.5 py-1.5 text-center`} data-id="element-5274" data-mp="a97odq3dqok8">
      <p className="text-base font-bold tabular-nums leading-none" data-id="element-5275" data-mp="67m4vlwcbc12">{count}</p>
      <p className="text-[9px] font-medium mt-0.5 truncate" data-id="element-5276" data-mp="3s8jrlz4n6jrt">{label}</p>
    </div>;
}
function LegendDot({
  color,
  label
}: {
  color: 'emerald' | 'amber' | 'orange' | 'red' | 'gray';
  label: string;
}) {
  const cls = {
    emerald: 'bg-emerald-400',
    amber: 'bg-amber-400',
    orange: 'bg-orange-400',
    red: 'bg-red-500',
    gray: 'bg-gray-300'
  }[color];
  return <div className="flex items-center gap-1.5" data-id="element-5277" data-mp="38izxzvx4li4d">
      <div className={`w-2 h-2 rounded-full ${cls}`} data-id="element-5278" data-mp="38ldr3z88wk9a" />
      <span className="text-[10px] text-gray-500 uppercase tracking-wider" data-id="element-5279" data-mp="3dim8vyphema0">
        {label}
      </span>
    </div>;
}
function EmptyTile({
  icon,
  text
}: {
  icon: React.ReactNode;
  text: string;
}) {
  return <div className="bg-gray-50 rounded-lg p-6 text-center border border-gray-100" data-id="element-5280" data-mp="34ipfxkw27195">
      <div className="mx-auto mb-2 w-fit" data-id="element-5281" data-mp="xqyfgd9ofbvy">{icon}</div>
      <p className="text-xs text-gray-600" data-id="element-5282" data-mp="xp54wmvy07io">{text}</p>
    </div>;
}
// ─── Skeleton states ─────────────────────────────────────────────────────
function SkeletonHero() {
  return <div className="rounded-2xl border border-gray-200 bg-gray-50 p-6 animate-pulse" data-id="element-5283" data-mp="2ba8i5fqx5b6j">
      <div className="flex items-center gap-6" data-id="element-5284" data-mp="26covpsk2hs3i">
        <div className="w-24 h-24 rounded-full bg-gray-200" data-id="element-5285" data-mp="26dzz3r9loxu3" />
        <div className="flex-1 space-y-2" data-id="element-5286" data-mp="21jduq81xs5ji">
          <div className="h-3 w-32 bg-gray-200 rounded" data-id="element-5287" data-mp="1xjbqps8qb012" />
          <div className="h-5 w-48 bg-gray-200 rounded" data-id="element-5288" data-mp="1xitxl4hd51xd" />
          <div className="h-3 w-full bg-gray-200 rounded" data-id="element-5289" data-mp="1sks5ju1yls9r" />
          <div className="h-3 w-3/4 bg-gray-200 rounded" data-id="element-5290" data-mp="1si5ydvizdes2" />
        </div>
      </div>
    </div>;
}
function SkeletonMatrix() {
  return <div className="rounded-xl border border-gray-200 bg-white p-5 animate-pulse" data-id="element-5291" data-mp="1ilt3iubp8uvq">
      <div className="h-4 w-48 bg-gray-200 rounded mb-4" data-id="element-5292" data-mp="1inw2i3utxycl" />
      <div className="grid grid-cols-8 gap-1.5" data-id="element-5293" data-mp="1elsob7gs0fnm">
        {Array.from({
        length: 24
      }).map((_, i) => <div key={i} className="h-12 bg-gray-100 rounded-lg" data-id="element-5294" data-mp="3czkdjuxxxgrp" data-mpi={i} />)}
      </div>
    </div>;
}